/**
 * F7 / M.3 — Export menu for the Obsidian Vault window.
 *
 * M.3 refactor: the three export destinations now live in
 * `src/lib/vault/export/targets/` behind the ExportTarget interface.
 * This component is a thin presenter — it builds the PortableSite
 * (via the same hydrateAssetBytes pipeline G.2 introduced) and renders
 * the registry as Win95-styled menu rows.
 *
 * Adding a new target = drop a file in targets/, register in
 * targets/index.ts. No edits here.
 *
 * G.2 — Binary asset bytes are hydrated from Convex Storage before
 * each export when `assetRows` + `urlForAsset` are supplied.
 */

import { useState, type ReactElement } from 'react';
import { pushGraphErrorToast, pushGraphInfoToast } from '../../lib/graph/useGraphErrorToast';
import type { PortableAsset, PortableSite } from '../../lib/portable-site';
import {
  hydrateAssetBytes,
  type AssetRowInput,
} from '../../lib/vault/export/hydrateAssetBytes';
import {
  ALL_TARGETS,
  isShowDirectoryPickerWritableAvailable,
  type ExportEnvironment,
  type ExportTarget,
} from '../../lib/vault/export/targets';
import { testIds } from '../../lib/testIds';
import type { ParsedFlow, ParsedNote } from '../../lib/vault/parse';

export interface VaultExportMenuProps {
  notes: ReadonlyArray<ParsedNote>;
  flows: ReadonlyArray<ParsedFlow>;
  sourceLabel: string;
  /**
   * G.2 — Optional list of vault asset rows. When supplied (live mode),
   * bytes are fetched via `urlForAsset` and included in every export.
   * Sample-vault demos pass undefined → exports carry notes + flows only.
   */
  assetRows?: ReadonlyArray<AssetRowInput>;
  /** Resolver from row id → Convex Storage signed URL (or null). */
  urlForAsset?: (assetId: string) => Promise<string | null>;
  /** True when the signed-in user has the operator role. */
  isOperator?: boolean;
  /** Operator-only fetch: kicks the Function-App provisioner. */
  onExportToSharePointSite?: (
    site: PortableSite,
  ) => Promise<{ jobId?: string } | void>;
}

/** Map ExportTarget.id → menu testIds (Win95-styled). */
const TEST_ID_FOR_TARGET: Record<string, string | undefined> = {
  folder: testIds.obsidianVault.exportFolder,
  zip: testIds.obsidianVault.exportZip,
  'sharepoint-site': testIds.obsidianVault.exportSharePoint,
};

export function VaultExportMenu({
  notes,
  flows,
  sourceLabel,
  assetRows,
  urlForAsset,
  isOperator,
  onExportToSharePointSite,
}: VaultExportMenuProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const env: ExportEnvironment = {
    fsapAvailable: isShowDirectoryPickerWritableAvailable(),
    isOperator: !!isOperator,
    sharePointProvisionerWired: !!onExportToSharePointSite,
  };
  const visibleTargets = ALL_TARGETS.filter((t) => t.isVisible(env));

  const buildSite = async (): Promise<PortableSite> => {
    let assets: PortableAsset[] = [];
    if (assetRows && assetRows.length > 0 && urlForAsset) {
      const result = await hydrateAssetBytes(assetRows, { urlForAsset });
      assets = result.assets;
      if (result.skipped.length > 0) {
        pushGraphErrorToast(
          new Error(
            `Skipped ${result.skipped.length} asset${result.skipped.length === 1 ? '' : 's'} during export (no storageId or fetch failed).`,
          ),
        );
      }
    }
    return {
      notes: notes.map((n) => ({
        path: n.path,
        name: n.name,
        body: n.body,
        frontmatter: n.frontmatter,
        wikilinks: n.wikilinks,
        embeds: n.embeds,
        markdownLinks: n.markdownLinks,
        tags: n.tags,
        callouts: n.callouts,
      })),
      flows: flows.map((f) => ({
        path: f.path,
        json: f.raw,
        parsed: {
          version: f.version,
          flowName: f.flowName,
          nodes: f.nodes,
          edges: f.edges,
        },
      })),
      assets,
      source: 'live-vault',
      schemaProfile: 'freeform',
      schemaIssues: [],
      sourceLabel,
    };
  };

  const handleRun = async (target: ExportTarget) => {
    setBusy(true);
    try {
      const site = await buildSite();
      const result = await target.run(site, { onExportToSharePointSite });
      pushGraphInfoToast(result.summary);
      setOpen(false);
    } catch (err) {
      pushGraphErrorToast(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Export vault"
        aria-expanded={open}
        data-testid={testIds.obsidianVault.exportMenuButton}
        disabled={busy}
        className="px-2 py-0.5 bg-[#ece9d8] border border-[#7f9db9] text-[12px] cursor-pointer disabled:opacity-50"
      >
        Export ▾
      </button>
      {open ? (
        <div
          role="menu"
          aria-label="Vault export options"
          data-testid={testIds.obsidianVault.exportMenuPanel}
          className="absolute right-0 top-full mt-1 z-50 bg-white text-black border-2 border-black shadow-[2px_2px_0_0_#808080] p-1 text-[12px] min-w-[14rem]"
        >
          {visibleTargets.map((target) => {
            const enabled = target.isEnabled(env);
            const tooltip = target.tooltip?.(env);
            return (
              <button
                key={target.id}
                type="button"
                role="menuitem"
                disabled={busy || !enabled}
                data-testid={TEST_ID_FOR_TARGET[target.id]}
                title={tooltip}
                onClick={() => handleRun(target)}
                className="block w-full text-left px-2 py-1 hover:bg-[#fff8d6] disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {target.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
